import { useEffect, useState } from "react";

const CONTENT_ROOT = `${process.env.PUBLIC_URL || ""}/content`;
const emptyLibrary = {
  about: { eyebrow: "", title: [], story: [], stats: [], credentials: [] },
  caseStudies: [],
  blogPosts: [],
  faqs: [],
  products: [],
  services: [],
};
let cachedLibrary = null;

const assetPath = (section, file) => (section ? `${CONTENT_ROOT}/${section}/${file}` : `${CONTENT_ROOT}/${file}`);
const productPathFor = (entry) => (entry.endsWith(".md") ? entry : `${entry}/index.md`);
const productFallbackPathFor = (entry) => (entry.endsWith(".md") ? entry : `${entry}.md`);
const sourceSlugFor = (file) => file.replace(/\/index\.md$/, "").replace(/\.md$/, "").split("/").pop();
const markdownImageMatch = (line) => line.match(/^!\[(.*?)\]\((.*?)\)$/);
const orderedListMatch = (line) => line.match(/^(\d+)\.\s+(.+)$/);

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Unable to load ${url}`);
  return response.json();
};

const fetchText = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Unable to load ${url}`);
  return response.text();
};

const parseValue = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (["[", "{"].includes(trimmed[0])) return JSON.parse(trimmed);
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return JSON.parse(trimmed);
  return trimmed;
};

const parseMarkdownFile = (source) => {
  const match = source.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { meta: {}, markdown: source.trim() };

  const meta = match[1].split(/\r?\n/).reduce((acc, line) => {
    const splitAt = line.indexOf(":");
    if (splitAt === -1) return acc;
    acc[line.slice(0, splitAt).trim()] = parseValue(line.slice(splitAt + 1));
    return acc;
  }, {});

  return { meta, markdown: match[2].trim() };
};

const markdownBlocks = (markdown) => {
  const blocks = [];
  let paragraph = [];
  let code = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: "p", text: paragraph.join(" ") });
    paragraph = [];
  };

  // Convert the small content format into the shape the existing UI expects.
  markdown.split(/\r?\n/).forEach((line) => {
    if (code) {
      if (line.startsWith("```")) {
        blocks.push({ type: "code", language: code.language, text: code.lines.join("\n") });
        code = null;
        return;
      }
      code.lines.push(line);
      return;
    }

    if (line.startsWith("```")) {
      flushParagraph();
      code = { language: line.slice(3).trim(), lines: [] };
      return;
    }

    if (line.startsWith("## ")) {
      flushParagraph();
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      return;
    }

    if (line.startsWith("### ")) {
      flushParagraph();
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      return;
    }

    if (line.startsWith("#### ")) {
      flushParagraph();
      blocks.push({ type: "h4", text: line.slice(5).trim() });
      return;
    }

    const image = markdownImageMatch(line.trim());
    if (image) {
      flushParagraph();
      blocks.push({ type: "img", alt: image[1], src: image[2] });
      return;
    }

    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      blocks.push({ type: "li", text: line.replace(/^[-*]\s+/, "").trim() });
      return;
    }

    const orderedList = orderedListMatch(line.trim());
    if (orderedList) {
      flushParagraph();
      blocks.push({ type: "ol", number: Number(orderedList[1]), text: orderedList[2].trim() });
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      return;
    }

    paragraph.push(line.trim());
  });

  flushParagraph();
  if (code) blocks.push({ type: "code", language: code.language, text: code.lines.join("\n") });
  return blocks;
};

const plainText = (markdown) => markdownBlocks(markdown).filter((block) => block.type === "p").map((block) => block.text).join("\n\n");

const blocksForHeading = (blocks, heading) => {
  const start = blocks.findIndex((block) => block.type === "h2" && block.text.toLowerCase() === heading);
  if (start === -1) return [];
  const next = blocks.findIndex((block, index) => index > start && block.type === "h2");
  return blocks.slice(start + 1, next === -1 ? blocks.length : next);
};

const bodyTextFor = (blocks, heading) =>
  blocksForHeading(blocks, heading)
    .filter((block) => block.type === "p")
    .map((block) => block.text)
    .join("\n\n");

const sectionItems = (blocks) => {
  const items = [];
  let current = null;

  blocks.forEach((block) => {
    if (block.type === "h3") {
      current = { title: block.text, desc: "", bullets: [], code: null };
      items.push(current);
      return;
    }
    if (!current) return;
    if (block.type === "p") current.desc = current.desc ? `${current.desc}\n\n${block.text}` : block.text;
    if (block.type === "li") current.bullets.push(block.text);
    if (block.type === "code") current.code = block;
  });

  return items;
};

// Product pages use every non-core H2 as a repeatable detail section.
const productSections = (blocks) => {
  const coreSections = ["hero", "capabilities", "how it works", "faq", "faqs"];
  const headings = blocks
    .map((block, index) => ({ ...block, index }))
    .filter((block) => block.type === "h2");

  return headings
    .filter((heading) => !coreSections.includes(heading.text.toLowerCase()))
    .map((heading) => {
      const next = headings.find((candidate) => candidate.index > heading.index);
      const sectionBlocks = blocks.slice(heading.index + 1, next ? next.index : blocks.length);
      const intro = sectionBlocks.find((block) => block.type === "p")?.text || "";
      return {
        title: heading.text,
        intro,
        items: sectionItems(sectionBlocks),
        bullets: sectionBlocks.filter((block) => block.type === "li").map((block) => block.text),
        code: sectionBlocks.find((block) => block.type === "code") || null,
      };
    });
};

const productFaqs = (blocks) => sectionItems(blocksForHeading(blocks, "faq")).concat(sectionItems(blocksForHeading(blocks, "faqs")));

const productHero = (blocks, meta) => {
  const heroBlocks = blocksForHeading(blocks, "hero");
  const paragraphs = heroBlocks.filter((block) => block.type === "p").map((block) => block.text);
  const eyebrow = heroBlocks.find((block) => block.type === "h3")?.text || meta.eyebrow || "";
  const titleLines = meta.heroTitleLines || (paragraphs[0] || meta.title || "").split(/\s+\|\s+/).filter(Boolean);

  return {
    eyebrow,
    titleLines,
    highlightLine: meta.heroHighlightLine ?? meta.highlightLine ?? 1,
    description: paragraphs[1] || meta.description || "",
    proofPoints: heroBlocks.filter((block) => block.type === "li").map((block) => block.text),
    image: meta.heroImage || "",
    primaryCta: meta.heroPrimaryCta || "Explore the Product",
    secondaryCta: meta.heroSecondaryCta || "Book a Demo",
  };
};

const loadMarkdownGroup = async (section, mapper) => {
  const files = await fetchJson(assetPath(section, "index.json"));
  const entries = await Promise.all(
    files.map(async (file) => {
      const { meta, markdown } = parseMarkdownFile(await fetchText(assetPath(section, file)));
      return mapper(meta, markdown, file);
    })
  );
  return entries;
};

const loadProductGroup = async () => {
  const entries = await fetchJson(assetPath("products", "index.json"));
  const seen = new Set();
  const products = [];

  for (const entry of entries) {
    let file = productPathFor(entry);
    let source = null;
    try {
      source = await fetchText(assetPath("products", file));
    } catch (error) {
      file = productFallbackPathFor(entry);
      source = await fetchText(assetPath("products", file));
    }
    const { meta, markdown } = parseMarkdownFile(source);
    const blocks = markdownBlocks(markdown);
    const sourceSlug = sourceSlugFor(file);
    const requestedSlug = meta.slug || sourceSlug;
    const slug = seen.has(requestedSlug) ? sourceSlug : requestedSlug;
    seen.add(slug);
    products.push({
      ...meta,
      slug,
      sourcePath: file,
      flagship: meta.flagship === true,
      title: meta.title || sourceSlug,
      titleLines: meta.titleLines || [meta.title || sourceSlug],
      heroStats: meta.heroStats || [],
      capabilitiesHeading: meta.capabilitiesHeading || "Capabilities",
      flowHeading: meta.flowHeading || "How it works",
      hero: productHero(blocks, meta),
      capabilities: sectionItems(blocksForHeading(blocks, "capabilities")),
      flow: sectionItems(blocksForHeading(blocks, "how it works")),
      sections: productSections(blocks),
      faqs: productFaqs(blocks),
    });
  }

  return products;
};

export const loadContentLibrary = async () => {
  if (cachedLibrary) return cachedLibrary;

  const [aboutSource, caseStudies, blogPosts, products, services] = await Promise.all([
    // About is a single section file while other content types are indexed groups.
    fetchText(assetPath("", "about.md")),
    loadMarkdownGroup("case-studies", (meta, markdown) => {
      const blocks = markdownBlocks(markdown);
      return {
        ...meta,
        challenge: bodyTextFor(blocks, "the challenge"),
        solution: bodyTextFor(blocks, "the solution"),
        outcome: bodyTextFor(blocks, "the outcome"),
      };
    }),
    loadMarkdownGroup("blog", (meta, markdown) => ({
      ...meta,
      body: markdownBlocks(markdown),
    })),

    loadProductGroup(),
    loadMarkdownGroup("services", (meta, markdown) => ({
      ...meta,
      desc: plainText(markdown),
    })),
  ]);

  const aboutFile = parseMarkdownFile(aboutSource);

  cachedLibrary = {
    about: {
      ...aboutFile.meta,
      story: markdownBlocks(aboutFile.markdown).filter((block) => block.type === "p"),
    },
    caseStudies,
    blogPosts: blogPosts.sort((a, b) => new Date(b.date) - new Date(a.date)),
    // Product FAQs are parsed from each product markdown file.
    faqs: [],
    products,
    services,
  };
  return cachedLibrary;
};

export const useContentLibrary = () => {
  const [state, setState] = useState({
    data: cachedLibrary || emptyLibrary,
    loading: !cachedLibrary,
    error: null,
  });

  useEffect(() => {
    let mounted = true;
    loadContentLibrary()
      .then((data) => mounted && setState({ data, loading: false, error: null }))
      .catch((error) => mounted && setState({ data: emptyLibrary, loading: false, error }));
    return () => {
      mounted = false;
    };
  }, []);

  return state;
};

export const findCaseStudy = (library, slug) => library.caseStudies.find((c) => c.slug === slug);
export const findBlogPost = (library, slug) => library.blogPosts.find((b) => b.slug === slug);
export const findProduct = (library, slug) => library.products.find((p) => p.slug === slug);
